import type React from 'react';
import {Autocomplete, TextField} from '@mui/material';
import {useDebouncedSearch} from '~/hooks/useDebouncedSearch';
import {useProfileSuppliersQuery} from '../../../hooks/useCategoryProfileQueries';
import type {SupplierRow} from '../../../types/categoryProfileTypes';

type Props = {
    databagId: number;
    path: string;
    /** Suppliers already in the list; they are not offered again. */
    excluded: (number | null)[];
    onAdd: (supplier: SupplierRow) => void;
    money: (v: number) => string;
};

/** Search the category's suppliers (largest first) and pick one to add. */
export const AddSupplierField: React.FC<Props> = ({databagId, path, excluded, onAdd, money}) => {
    const search = useDebouncedSearch(300);
    const {data, isFetching} = useProfileSuppliersQuery(databagId, {
        path, ordering: '-spend', page: 0, pageSize: 20, search: search.debounced, country: '', cut: 100, group: 'supplier',
    });
    const options = (data?.rows ?? []).filter(r => r.supplierId !== null && !excluded.includes(r.supplierId));

    return (
        <Autocomplete
            size="small" sx={{maxWidth: 420}} options={options} loading={isFetching} value={null}
            inputValue={search.input} filterOptions={x => x} blurOnSelect clearOnBlur
            getOptionLabel={o => o.name} isOptionEqualToValue={(a, b) => a.supplierId === b.supplierId}
            onInputChange={(_, v, reason) => reason !== 'reset' && search.setInput(v)}
            onChange={(_, v) => {
                if (!v) return;
                onAdd(v);
                search.clear();
            }}
            renderOption={(props, o) => (
                <li {...props} key={o.supplierId}>
                    {`${o.name} · ${money(o.spend)}${o.spendSharePct === null ? '' : ` (${o.spendSharePct.toFixed(1)}%)`}`}
                </li>
            )}
            noOptionsText={search.debounced ? 'No matching supplier in this category' : 'Type to search the suppliers'}
            renderInput={params => <TextField {...params} placeholder="Add a supplier" aria-label="Add a supplier"/>}
        />
    );
};
